/**
 * Guardar un apartado editado desde Vender (2026-10-26).
 *
 * Lo pidió el dueño: «sería mejor que se pudieran editar». El apartado se
 * abre en la caja con lo que tenía; se agrega o se quita lo que haga falta, y
 * en lugar del cobro sale esto.
 *
 * ⚠️ AQUÍ NO SE COBRA. Lo que ya pagó pasa entero a la versión nueva y lo que
 * falte queda como saldo, que se abona desde Apartados como siempre. Mezclar
 * un cobro aquí obligaría a repetir toda la cuenta del cobro (vuelto, IGTF,
 * monedas) para un caso que ya tiene su camino.
 *
 * Los números son para entender antes de guardar: los que valen los calcula el
 * servidor con el catálogo de hoy, igual que en una venta.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save } from "lucide-react";
import { Boton, Nota, cx } from "@/components/ui";
import { mensajeDeError } from "@/lib/api";
import { apartados } from "@/lib/endpoints";
import { formatMoney, toNumber, type FormatoMoneda } from "@/lib/money";
import type { ApartadoDetalle, Tax } from "@/lib/types";
import { redondearMonto, useCarrito, type TotalesCarrito } from "@/stores/carrito";
import { useConexion } from "@/stores/conexion";
import { aviso } from "@/stores/ui";
import { recargoDeImpuestosDePedido } from "@/features/pos/impuestosDePedido";

export function GuardarApartado({
  totales,
  formato,
  impuestosDePedido,
  onCerrar,
  onGuardado,
}: {
  totales: TotalesCarrito;
  formato: FormatoMoneda;
  impuestosDePedido: Tax[];
  onCerrar: () => void;
  onGuardado: (apartado: ApartadoDetalle) => void;
}) {
  const carrito = useCarrito();
  const qc = useQueryClient();
  const enLinea = useConexion((s) => s.enLinea);
  const editando = carrito.editandoApartado;

  /*
    Solo los impuestos que van sobre el total (el IVA que enciende el cajero).
    Los que dependen de la moneda con que se paga (el IGTF) no aplican: aquí no
    se paga nada.
  */
  const sobreElTotal = impuestosDePedido.filter(
    (t) => carrito.impuestosPedido.includes(t.id) && t.currency_codes.length === 0,
  );
  const recargo = sobreElTotal.reduce(
    (n, t) =>
      n +
      recargoDeImpuestosDePedido({
        encendidos: [t],
        total: totales.total,
        entradoPorMoneda: {},
        exentoPorImpuesto: totales.exentoPorImpuesto,
        sumaDeLineas: totales.sumaDeLineas,
      }),
    0,
  );
  const total = redondearMonto(totales.total + recargo);
  const pagado = toNumber(editando?.pagado ?? "0");
  const queda = redondearMonto(total - pagado);
  const deMas = queda < -0.005;

  const guardar = useMutation({
    mutationFn: () => {
      if (!editando) throw new Error("No se está editando ningún apartado.");
      if (!enLinea) throw new Error("Para editar un apartado hace falta internet. Inténtalo cuando vuelva.");
      return apartados.editar(editando.id, {
        items: carrito.lineas.map((l) => ({
          item_id: l.itemId,
          variant_id: l.variantId,
          qty: String(l.cantidad),
          discount: l.descuento > 0 ? String(l.descuento) : undefined,
          staff_user_id: l.staffUserId,
          monto: l.monto != null ? String(l.monto) : undefined,
          opcion_ids: l.opciones?.length ? l.opciones.map((o) => o.id) : undefined,
        })),
        lista_precios_id: carrito.listaPreciosId ?? undefined,
        order_tax_ids: sobreElTotal.length > 0 ? sobreElTotal.map((t) => t.id) : undefined,
        discount_total: carrito.descuentoGlobal > 0 ? String(carrito.descuentoGlobal) : undefined,
        note: carrito.nota.trim() || null,
      });
    },
    onSuccess: (apartado) => {
      void qc.invalidateQueries({ queryKey: ["apartados"] });
      void qc.invalidateQueries({ queryKey: ["cuentas-por-cobrar"] });
      void qc.invalidateQueries({ queryKey: ["estado-cuenta"] });
      aviso.exito(
        `Apartado ${apartado.number} guardado`,
        toNumber(apartado.saldo) > 0.005
          ? `Queda debiendo ${formatMoney(apartado.saldo, formato)}.`
          : "Está pagado del todo: ya se puede entregar.",
      );
      onGuardado(apartado);
    },
    onError: (e) => aviso.error("No se pudo guardar el apartado", mensajeDeError(e)),
  });

  if (!editando) return null;

  return (
    <div className="space-y-4">
      <div>
        <p className="rotulo">Editando el apartado {editando.numero}</p>
        <p className="mt-1 text-sm text-muted">
          Lo que ya pagó se mantiene. Lo que falte se abona desde Apartados.
        </p>
      </div>

      <dl className="divide-y divide-line rounded-[var(--radius-card)] border border-line">
        <Fila etiqueta="Nuevo total" valor={formatMoney(total, formato)} />
        <Fila etiqueta="Ya pagó" valor={formatMoney(pagado, formato)} tono="exito" />
        <Fila
          etiqueta={deMas ? "Pagó de más" : "Queda debiendo"}
          valor={queda <= 0.005 && !deMas ? "Nada" : formatMoney(Math.abs(queda), formato)}
          tono={deMas ? "peligro" : "fuerte"}
        />
      </dl>

      {deMas ? (
        <Nota tono="peligro">
          Lo que ya pagó es más que el nuevo total. Agregue artículos, o anule el apartado desde
          Apartados para decidir qué se hace con lo pagado.
        </Nota>
      ) : queda <= 0.005 ? (
        <Nota tono="exito">Queda pagado del todo: se podrá entregar al guardar.</Nota>
      ) : null}

      <div className="flex flex-col gap-2 sm:flex-row-reverse">
        <Boton
          ancho
          tamano="lg"
          icono={<Save size={17} />}
          onClick={() => guardar.mutate()}
          cargando={guardar.isPending}
          disabled={deMas || carrito.lineas.length === 0}
        >
          Guardar el apartado
        </Boton>
        <Boton ancho variante="secundario" icono={<ArrowLeft size={16} />} onClick={onCerrar}>
          Seguir editando
        </Boton>
      </div>
    </div>
  );
}

function Fila({
  etiqueta,
  valor,
  tono = "normal",
}: {
  etiqueta: string;
  valor: string;
  tono?: "normal" | "fuerte" | "exito" | "peligro";
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-3.5 py-2.5">
      <dt className="text-sm text-muted">{etiqueta}</dt>
      <dd
        className={cx(
          "tabular text-sm",
          tono === "normal" && "font-medium text-ink",
          tono === "fuerte" && "text-base font-semibold text-ink",
          tono === "exito" && "font-medium text-success",
          tono === "peligro" && "text-base font-semibold text-danger",
        )}
      >
        {valor}
      </dd>
    </div>
  );
}
